import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { TabbedPageShell, buildTabOptions } from '../../components/layout/tabbedPageShell';
import { TREATMENT_PLAN_BASE_PATH } from '../../constants';
import { FormReadOnlyProvider } from '../common';
import { TreatmentPlanV3Header } from './components';
import { TREATMENT_PLAN_V3_TAB_CONFIGS, TREATMENT_PLAN_V3_TAB_IDS } from './constants';
import { useIsTreatmentPlanV3ReadOnly, useTreatmentPlanV3TabStatus } from './hooks';
import TreatmentPlanV3Routes from './TreatmentPlanV3Routes';

export default function TreatmentPlanV3() {
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const isReadOnly = useIsTreatmentPlanV3ReadOnly();
  const {
    assessment,
    child,
    crisis,
    discharge,
    education,
    family,
    medication,
    referral,
    therapy,
    treatment,
  } = useTreatmentPlanV3TabStatus();
  const tabOptions = useMemo(
    () =>
      buildTabOptions(
        TREATMENT_PLAN_V3_TAB_CONFIGS,
        `${TREATMENT_PLAN_BASE_PATH}/${treatmentPlanId}`,
        (tabId) => {
          if (tabId === TREATMENT_PLAN_V3_TAB_IDS.ASSESSMENT) return assessment;
          if (tabId === TREATMENT_PLAN_V3_TAB_IDS.CHILD) return child;
          if (tabId === TREATMENT_PLAN_V3_TAB_IDS.CRISIS) return crisis;
          if (tabId === TREATMENT_PLAN_V3_TAB_IDS.EDUCATION) return education;
          if (tabId === TREATMENT_PLAN_V3_TAB_IDS.FAMILY) return family;
          if (tabId === TREATMENT_PLAN_V3_TAB_IDS.MEDICATION) return medication;
          if (tabId === TREATMENT_PLAN_V3_TAB_IDS.REFERRAL) return referral;
          if (tabId === TREATMENT_PLAN_V3_TAB_IDS.THERAPY) return therapy;
          if (tabId === TREATMENT_PLAN_V3_TAB_IDS.DISCHARGE) return discharge;
          if (tabId === TREATMENT_PLAN_V3_TAB_IDS.TREATMENT) return treatment;
          return undefined;
        },
      ),
    [
      assessment,
      child,
      crisis,
      discharge,
      education,
      family,
      medication,
      referral,
      therapy,
      treatment,
      treatmentPlanId,
    ],
  );

  return (
    <FormReadOnlyProvider readOnly={isReadOnly} showValidationErrors>
      <TabbedPageShell
        tabsAriaLabel="Treatment plan sections"
        tabOptions={tabOptions}
        errorLabel="Section has validation errors"
        successLabel="Section is complete"
        header={<TreatmentPlanV3Header />}
      >
        <TreatmentPlanV3Routes />
      </TabbedPageShell>
    </FormReadOnlyProvider>
  );
}
